import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth, DEV_BYPASS_ENABLED } from "@/lib/auth-context";
import { Section } from "@/components/Section";
import { ArrowRight, Lock, Sparkles, Mail, Loader2, KeyRound } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { setSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/portal";

  const sendMagicLink = async (e) => {
    e?.preventDefault?.();
    if (!email) {
      toast.error("Enter your work email.");
      return;
    }
    try {
      setSubmitting(true);
      await api.post("/auth/magic-link/request", {
        email,
        role: "clinic",
        redirect_path: from,
      });
      setLinkSent(true);
      toast.success("Check your email — we just sent you a one-tap sign-in link.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Email and password are required.");
      return;
    }
    try {
      setSubmitting(true);
      const r = await api.post("/auth/clinic/login", { email, password });
      setSession(r.data.token, r.data.user);
      toast.success(`Welcome back, ${r.data.user.clinic_name || r.data.user.email}`);
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const devBypass = async () => {
    try {
      setSubmitting(true);
      const r = await api.post("/auth/dev/clinic-bypass");
      setSession(r.data.token, r.data.user);
      toast.success("Dev clinic session active.");
      navigate("/portal", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="login-page">
      <Section className="pt-28">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.22em] font-semibold text-blue-600 mb-4">Clinic portal</div>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter text-slate-900 leading-[1.02]">
              Welcome back.
            </h1>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Log in to access your approved program catalog, request expansions, and coordinate with the Nodera operations team.
            </p>
            <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50/60 p-5 space-y-3 text-sm">
              <div className="flex items-start gap-3">
                <Lock className="w-4 h-4 mt-0.5 text-slate-500" />
                <div>
                  <div className="font-display font-semibold text-slate-900">Don't have access yet?</div>
                  <p className="text-slate-600 leading-relaxed mt-1">
                    The portal is invite-only. Submit a request and our team will vet your clinic for activation.
                  </p>
                  <Link
                    to="/request-access"
                    data-testid="login-request-access-link"
                    className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 hover:text-blue-900"
                  >
                    Request portal access <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            {linkSent ? (
              <div
                data-testid="login-magic-link-sent"
                className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-6 lg:p-8 space-y-4 shadow-sm"
              >
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center text-emerald-700">
                    <Mail className="w-5 h-5" />
                  </span>
                  <div>
                    <div className="font-display font-bold text-slate-900">Check your inbox</div>
                    <div className="text-sm text-slate-600">
                      We sent a one-tap sign-in link to <b>{email}</b>. It expires in 20 minutes.
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setLinkSent(false)}
                  data-testid="login-magic-link-back-btn"
                  className="text-xs font-semibold text-slate-700 hover:text-slate-900"
                >
                  ← Use a different email or sign in with password
                </button>
              </div>
            ) : (
              <form
                onSubmit={sendMagicLink}
                data-testid="login-form"
                className="rounded-2xl border border-slate-200 bg-white p-6 lg:p-8 space-y-4 shadow-sm"
              >
                <Field label="Work email" required>
                  <input
                    type="email"
                    required
                    data-testid="login-email-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={inputCls}
                    placeholder="you@yourclinic.com"
                  />
                </Field>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="login-magic-link-btn"
                  className="w-full inline-flex items-center justify-center gap-2 bg-slate-900 text-white rounded-full px-6 py-3.5 text-sm font-semibold hover:bg-slate-800 transition-colors disabled:opacity-50"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Sending link…
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4" /> Email me a one-tap sign-in link
                    </>
                  )}
                </button>
                <p className="text-[11px] text-slate-500 leading-relaxed text-center">
                  Sessions stay active for 30 days on trusted devices.
                </p>

                <div className="pt-3 border-t border-slate-100">
                  {showPassword ? (
                    <div className="space-y-3" data-testid="login-password-block">
                      <Field label="Password">
                        <input
                          type="password"
                          required
                          data-testid="login-password-input"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className={inputCls}
                        />
                      </Field>
                      <button
                        type="button"
                        onClick={submit}
                        disabled={submitting}
                        data-testid="login-submit-btn"
                        className="w-full inline-flex items-center justify-center gap-2 bg-white border border-slate-200 text-slate-900 rounded-full px-6 py-3 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
                      >
                        <KeyRound className="w-4 h-4" /> Log in with password
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowPassword(false)}
                        className="block w-full text-[11px] text-slate-500 hover:text-slate-900"
                      >
                        ← Hide password option
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowPassword(true)}
                      data-testid="login-show-password-btn"
                      className="block w-full text-center text-xs font-semibold text-slate-500 hover:text-slate-900"
                    >
                      Or sign in with a password
                    </button>
                  )}
                </div>

                {DEV_BYPASS_ENABLED && (
                  <div className="pt-3 border-t border-slate-100">
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-semibold text-amber-700">
                      <Sparkles className="w-3.5 h-3.5" /> Dev environment
                    </div>
                    <button
                      type="button"
                      onClick={devBypass}
                      disabled={submitting}
                      data-testid="login-dev-bypass-btn"
                      className="mt-2 w-full inline-flex items-center justify-center gap-2 bg-amber-50 text-amber-900 border border-amber-200 rounded-full px-5 py-3 text-sm font-semibold hover:bg-amber-100"
                    >
                      Skip auth (dev clinic)
                    </button>
                  </div>
                )}
              </form>
            )}
          </div>
        </div>
      </Section>
    </div>
  );
}

const inputCls =
  "w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-colors";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-slate-700 mb-1.5">
        {label} {required && <span className="text-blue-600">*</span>}
      </span>
      {children}
    </label>
  );
}
